import React, { useCallback, useEffect, useMemo } from 'react';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Toolbar } from './Toolbar';
import { EditorContent } from './EditorContent';
import { Preview } from '../Preview';
import { ArrowLeft, Eye, Save, Check, Edit2 } from 'lucide-react';

interface EditorProps {
  initialContent: string;
  onSave: (content: string) => Promise<void>;
  onPublish: () => Promise<void>;
  onBack: () => void;
  status: 'draft' | 'published';
  lastSaved: Date | null;
  isSaving: boolean;
}

export const Editor: React.FC<EditorProps> = ({
  initialContent,
  onSave,
  onPublish,
  onBack,
  status,
  lastSaved,
  isSaving
}) => {
  const [showPreview, setShowPreview] = React.useState(false);
  const [editMode, setEditMode] = React.useState(status === 'draft');
  const [isPublishing, setIsPublishing] = React.useState(false);

  const initialHTML = useMemo(() => marked.parse(initialContent || ''), [initialContent]);
  const turndownService = useMemo(() => new TurndownService(), []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Image,
      Placeholder.configure({
        placeholder: 'Start typing your instructions here...',
      }),
    ],
    content: initialHTML,
    // Remove autosave; saving happens only on Publish
    editable: editMode,
  });

  // No autosave; nothing to clean up

  useEffect(() => {
    if (editor) {
      editor.setEditable(editMode);
    }
  }, [editor, editMode]);

  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} min ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    return date.toLocaleDateString();
  };

  const handlePublish = async () => {
    try {
      if (editor) {
        setIsPublishing(true);
        console.log('Publishing content...');
        await onSave(turndownService.turndown(editor.getHTML()));
        await onPublish();
        // Exit edit mode after successful publish
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error publishing:', error);
      // In production, show error toast
      alert(`Failed to publish: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPublishing(false);
    }
  };

  if (showPreview && editor) {
    return (
      <Preview 
        content={turndownService.turndown(editor.getHTML())} 
        onBack={() => setShowPreview(false)} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>

            {/* Status pill */}
            <div className="flex items-center gap-2 text-xs">
              {status === 'published' && !editMode ? (
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full" style={{ backgroundColor: 'color-mix(in oklab, var(--accent) 12%, white)', color: 'var(--accent)', border: '1px solid color-mix(in oklab, var(--accent) 30%, white)' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--accent)' }} />
                  Published
                </span>
              ) : status === 'published' && editMode ? (
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full" style={{ backgroundColor: 'color-mix(in oklab, var(--accent) 12%, white)', color: 'var(--accent)', border: '1px solid color-mix(in oklab, var(--accent) 30%, white)' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--accent)' }} />
                  Editing published
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full" style={{ backgroundColor: 'color-mix(in oklab, var(--accent) 12%, white)', color: 'var(--accent)', border: '1px solid color-mix(in oklab, var(--accent) 30%, white)' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--accent)' }} />
                  Draft
                </span>
              )}

              {lastSaved && (
                <span className="text-slate-500">Last saved {formatLastSaved(lastSaved)}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>

            {status === 'published' && !editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-white"
                style={{ backgroundColor: 'var(--accent)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            ) : (
              <button
                onClick={handlePublish}
                disabled={isSaving || isPublishing}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
              >
                <Check className="w-4 h-4" />
                {isPublishing ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col">
        {editMode && <Toolbar editor={editor} />}
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
};